/**
 * Backlog #53: which sidebar area is selected, remembered per person, plus the badge counts on area icons.
 * The route decides the area; pages outside the areas (Settings, Help, Search, Assistant) keep the last one.
 */
import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { create } from 'zustand';
import { useAckList, useContentAckCount } from '@/features/acks/AcksPage';
import { api } from './api';
import { useAuth } from './auth';
import { useConversations, useWorkspaceId } from './hooks';
import { useNavAccess } from './navAccess';
import { areaForPath, areaKey, areaLanding, areaPageKey, areaShortcut, visibleAreas, type AreaId } from './navModel';
import { qk } from './queryClient';

const read = (k: string) => { try { return localStorage.getItem(k); } catch { return null; } };
const write = (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* private mode */ } };

const useAreaStore = create<{ area: AreaId | null; setArea: (a: AreaId) => void }>(set => ({ area: null, setArea: area => set({ area }) }));

/** Pure: the area to show for a route, given the remembered one and what's visible. */
export function selectArea(path: string, remembered: string | null | undefined, visible: AreaId[]): AreaId {
  const fromRoute = areaForPath(path);
  if (fromRoute && visible.includes(fromRoute)) return fromRoute;
  if (remembered && visible.includes(remembered as AreaId)) return remembered as AreaId;
  return visible[0] ?? 'chat';
}

export function useNavArea() {
  const { userId } = useAuth();
  const { can } = useNavAccess();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const stored = useAreaStore(s => s.area);
  const areas = useMemo(() => visibleAreas(can), [can]);
  const selected = selectArea(pathname, stored ?? read(areaKey(userId)), areas.map(a => a.id));
  const open = useCallback((id: AreaId) => {
    const a = areas.find(x => x.id === id);
    if (!a) return;
    useAreaStore.getState().setArea(id);
    write(areaKey(userId), id);
    navigate(areaLanding(a, read(areaPageKey(userId, id)), can));
  }, [areas, can, navigate, userId]);
  return { areas, selected, area: areas.find(a => a.id === selected) ?? areas[0], open };
}

/** Mounted once (AppShell): remembers the area and the last page in it on every navigation, and handles Alt+1…5. */
export function NavAreaTracker() {
  const { userId } = useAuth();
  const { pathname, search } = useLocation();
  const { areas, open } = useNavArea();
  useEffect(() => {
    const id = areaForPath(pathname);
    if (!id) return;
    useAreaStore.getState().setArea(id);
    write(areaKey(userId), id);
    write(areaPageKey(userId, id), pathname + search);
  }, [pathname, search, userId]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const k = areaShortcut(e);
      if (!k) return;
      const a = areas.find(x => x.key === k);
      if (!a) return;
      e.preventDefault();
      open(a.id);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [areas, open]);
  return null;
}

/** Per-page counts of things waiting for you: server counts (manager queues, your shifts, tasks incl. private ones) plus acks; and chat unread. */
export function useNavBadgeItems() {
  const ws = useWorkspaceId();
  const server = useQuery({ queryKey: qk.navBadges(ws ?? ''), queryFn: () => api.navBadges(ws!), enabled: Boolean(ws), refetchInterval: 60_000, staleTime: 30_000 });
  const acks = (useAckList('pending').data?.items.length ?? 0) + useContentAckCount();
  const convs = useConversations().data?.conversations;
  const chat = (convs ?? []).filter(c => c.mention_count > 0 || (c.kind !== 'channel' && c.unread_count > 0 && c.notify_level !== 'none')).length;
  const items = useMemo(() => {
    const out: Record<string, number> = { ...(server.data?.items ?? {}) };
    if (acks) out['/acknowledgements'] = acks;
    return out;
  }, [server.data, acks]);
  return { items, chat };
}

/** Badges per page and per sidebar area. */
export function useNavBadges() {
  const { areas } = useNavArea();
  const { items, chat } = useNavBadgeItems();
  return useMemo(() => {
    const byArea = {} as Record<AreaId, number>;
    for (const a of areas) byArea[a.id] = a.visible.reduce((n, i) => n + (items[i.to] ?? 0), 0);
    if (chat) byArea.chat = (byArea.chat ?? 0) + chat;
    return { item: (path: string) => items[path] ?? 0, area: (id: AreaId) => byArea[id] ?? 0, chat };
  }, [items, chat, areas]);
}
